import moment, { Moment } from 'moment';
import { formatDate, parseDate } from 'helpers/date';
import { isAfter } from 'date-fns';

import { DateRangeState } from './DatePicker';

type DateRange = {
  from: Date | null;
  to: Date | null;
};

type PresetDateRanges = {
  [key: string]: { from: Date; to: Date };
};

type PresetButtons = {
  key: string;
  label: string;
};

type StartEndOf = 'year' | 'month' | 'week' | 'day';

const today = moment(new Date());
const lastWeek = today.clone().subtract(7, 'days');
const lastMonth = today.clone().subtract(1, 'month');

const getStartOf = (startOf: StartEndOf, date: Moment): Date =>
  date.clone().startOf(startOf).toDate();

const getEndOf = (endOf: StartEndOf, date: Moment) =>
  date.clone().endOf(endOf).toDate();

const subtractDays = (days: number, date: Moment) =>
  date.clone().subtract(days, 'days').toDate();

const presetDateRanges: PresetDateRanges = {
  thisMonth: {
    from: getStartOf('month', today),
    to: today.clone().toDate(),
  },
  lastMonth: {
    from: getStartOf('month', lastMonth),
    to: getEndOf('month', lastMonth),
  },
  thisWeek: {
    from: getStartOf('week', today),
    to: today.clone().toDate(),
  },
  lastWeek: {
    from: getStartOf('week', lastWeek),
    to: getEndOf('week', lastWeek),
  },
  thisYear: {
    from: getStartOf('year', today),
    to: today.clone().toDate(),
  },
  pastNineDays: {
    from: subtractDays(9, today),
    to: today.clone().toDate(),
  },
  nineDaysBeforeLastWeek: {
    from: subtractDays(9, lastWeek.clone().startOf('week')),
    to: getEndOf('week', lastWeek),
  },
  pastThirtyDays: {
    from: subtractDays(30, today),
    to: today.clone().toDate(),
  },
  pastNinetyDays: {
    from: subtractDays(90, today),
    to: today.clone().toDate(),
  },
};

const PRESET_BUTTONS: PresetButtons[] = [
  {
    key: 'thisYear',
    label: 'This Year',
  },
  {
    key: 'lastMonth',
    label: 'Last Month',
  },
  {
    key: 'thisMonth',
    label: 'This Month',
  },
  {
    key: 'lastWeek',
    label: 'Last Week',
  },
  {
    key: 'thisWeek',
    label: 'This Week',
  },
];

const getInitialMonth = (): Date => moment().subtract(1, 'month').toDate();

const isValidSelection = (from: Date | null, day: Date) =>
  from && !moment(day).isBefore(from) && !moment(day).isAfter(today);

const isSelectingFirstDay = (
  { from, to }: DateRange,
  day: Date,
): boolean | Date => {
  const isDateRangeSelected = from && to;

  return !from || isDateRangeSelected || !isValidSelection(from, day);
};

const getInputValue = ({ from, to, enteredTo }: DateRangeState): string => {
  const formattedFrom = formatDate(from as Date, 'MM/DD/YY');
  const formattedTo = formatDate((to || enteredTo || from) as Date, 'MM/DD/YY');

  return `${formattedFrom} - ${formattedTo}`;
};

interface CheckedDateRange {
  isValid: boolean;
  dateRange?: {
    from: Date;
    to: Date;
    enteredTo: Date;
  };
}

const checkDateRange = (date: string): CheckedDateRange => {
  const [from, to] = date.split(' - ');
  const startDate = parseDate(from, 'MM/dd/yy');
  const endDate = parseDate(to, 'MM/dd/yy');

  if (startDate && endDate) {
    const isValidSelection =
      !isAfter(startDate, endDate) && !isAfter(endDate, new Date());

    if (isValidSelection) {
      const dateRange = {
        from: startDate,
        to: endDate,
        enteredTo: endDate,
      };

      return { dateRange, isValid: true };
    }
  }

  return { isValid: false };
};

export {
  today,
  lastWeek,
  DateRange,
  PresetDateRanges,
  PresetButtons,
  presetDateRanges,
  PRESET_BUTTONS,
  getInitialMonth,
  isSelectingFirstDay,
  getInputValue,
  checkDateRange,
  getStartOf,
  CheckedDateRange,
};
